/**
 * Security Alerts — things an administrator should look at now (admin only).
 *
 * Raised by the backend's alert rules on audit activity (a copied passkey, a
 * lockout, a burst of failed sign-ins, a new administrator...). Critical and high
 * alerts e-mail every admin and show a banner until one of them is marked handled
 * here, so the others know somebody has looked.
 */
import { useCallback, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../../services/auth'
import {
  acknowledgeSecurityAlert, listSecurityAlerts,
  type SecurityAlert, type SecurityAlertSeverity, type SecurityAlertStatus,
} from '../../services/admin'
import { formatLocalDateTime } from '../../utils/formatDateTime'
import PageHeading from '../../components/PageHeading'
import { SECURITY_ALERTS_HELP } from '../../content/pageHelp'
import { Pagination } from '../../components/Pagination'
import {
  isPageNumber, listStateKey, oneOf, usePageResetOnChange, usePageSize, useSessionState,
} from '../../hooks/useSessionState'

const KEY = listStateKey('securityAlerts')

const STATUS_OPTIONS: { value: SecurityAlertStatus; label: string }[] = [
  { value: 'open', label: 'Needs attention' },
  { value: 'acknowledged', label: 'Handled' },
  { value: 'all', label: 'All alerts' },
]

const SEVERITY_STYLE: Record<SecurityAlertSeverity, string> = {
  critical: 'bg-red-600 text-white',
  high: 'bg-red-100 text-red-800',
  medium: 'bg-amber-100 text-amber-800',
}

function AlertCard({ alert, onHandled }: { alert: SecurityAlert; onHandled: () => void }) {
  const [noting, setNoting] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function handle() {
    setBusy(true)
    setError('')
    try {
      await acknowledgeSecurityAlert(alert.id, note.trim())
      onHandled()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save')
      setBusy(false)
    }
  }

  return (
    <li className={`card ${alert.acknowledged_at ? 'opacity-75' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded px-2 py-0.5 text-xs font-semibold uppercase ${SEVERITY_STYLE[alert.severity]}`}>
              {alert.severity}
            </span>
            <h2 className="font-semibold text-gray-900">{alert.title}</h2>
          </div>
          <p className="mt-2 text-sm text-gray-700">{alert.summary}</p>
          <p className="mt-2 text-xs text-gray-500">
            {alert.occurrences > 1
              ? <>Seen {alert.occurrences} times · first {formatLocalDateTime(alert.created_at)} · latest {formatLocalDateTime(alert.last_seen_at)}</>
              : formatLocalDateTime(alert.created_at)}
            {alert.subject_email && <> · {alert.subject_email}</>}
            {alert.ip_address && <> · from {alert.ip_address}</>}
            {alert.emailed_at && <> · admins e-mailed</>}
          </p>
          {alert.acknowledged_at && (
            <p className="mt-1 text-xs text-gray-600">
              Handled {formatLocalDateTime(alert.acknowledged_at)}
              {alert.acknowledged_by_name && <> by {alert.acknowledged_by_name}</>}
              {alert.acknowledgement_note && <>: “{alert.acknowledgement_note}”</>}
            </p>
          )}
        </div>
        {!alert.acknowledged_at && !noting && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setNoting(true)}>
            Mark handled…
          </button>
        )}
      </div>
      {noting && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <label htmlFor={`alert-note-${alert.id}`} className="label text-xs">
            What did you find or do? (optional, kept with the alert)
          </label>
          <input
            id={`alert-note-${alert.id}`}
            className="input text-sm"
            maxLength={500}
            value={note}
            placeholder="e.g. Theresa mistyped her password; confirmed by phone"
            onChange={(e) => setNote(e.target.value)}
          />
          {error && <p role="alert" className="mt-1 text-xs text-red-700">{error}</p>}
          <div className="mt-2 flex gap-2">
            <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={handle}>
              {busy ? 'Saving…' : 'Mark handled'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setNoting(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </li>
  )
}

export default function SecurityAlertsPage() {
  const { user } = useAuth()
  const [items, setItems] = useState<SecurityAlert[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [status, setStatus] = useSessionState<SecurityAlertStatus>(
    KEY('status'), 'open', oneOf(STATUS_OPTIONS.map((o) => o.value)),
  )
  const [severity, setSeverity] = useSessionState<SecurityAlertSeverity | ''>(
    KEY('severity'), '', oneOf(['', 'critical', 'high', 'medium']),
  )
  const [page, setPage] = useSessionState(KEY('page'), 1, isPageNumber)
  const [pageSize, setPageSize] = usePageSize('securityAlerts', 25)
  usePageResetOnChange(JSON.stringify([status, severity, pageSize]), setPage)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await listSecurityAlerts({
        status, severity: severity || undefined, page, page_size: pageSize,
      })
      setItems(res.items)
      setTotal(res.total)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load alerts')
    } finally {
      setLoading(false)
    }
  }, [status, severity, page, pageSize])

  useEffect(() => { load() }, [load])

  if (!user?.is_superuser) return <Navigate to="/" replace />

  return (
    <div className="space-y-4">
      <PageHeading title="Security Alerts" help={SECURITY_ALERTS_HELP} />

      <div className="toolbar gap-x-4 gap-y-2">
        <select
          className="filter-control"
          aria-label="Which alerts to show"
          value={status}
          onChange={(e) => setStatus(e.target.value as SecurityAlertStatus)}
        >
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          className="filter-control"
          aria-label="Filter by severity"
          value={severity}
          onChange={(e) => setSeverity(e.target.value as SecurityAlertSeverity | '')}
        >
          <option value="">Any severity</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
        </select>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <p className="py-6 text-center text-sm text-gray-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="card py-6 text-center text-sm text-gray-500">
          {status === 'open' ? 'Nothing needs attention.' : 'No alerts match.'}
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((a) => <AlertCard key={a.id} alert={a} onHandled={load} />)}
        </ul>
      )}

      <Pagination
        page={page} totalPages={Math.ceil(total / pageSize)} onPageChange={setPage}
        total={total} pageSize={pageSize}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1) }}
      />
    </div>
  )
}
